import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { setLeadSource } from '@/lib/leadSource';

/**
 * PageHero — clean, short hero block for standalone pages.
 *
 * Sets a single H1 per page, an eyebrow tag and a compact paragraph,
 * with optional CTAs (book a demo, whatsapp, brochure).
 *
 * Props:
 *  - eyebrow      : short tag above H1 (e.g. "Chi siamo")
 *  - title_a      : first part of the title (black/ink)
 *  - title_b      : second part of the title (teal accent)
 *  - subtitle     : 1–2 lines short paragraph
 *  - ctas         : array of CTA keys → ['demo','brochure','whatsapp']
 *  - source       : lead-source attribution (e.g. "about-hero")
 *  - testId       : root data-testid
 */
export const PageHero = ({
  eyebrow,
  title_a,
  title_b,
  subtitle,
  ctas = ['demo', 'whatsapp'],
  source = 'page-hero',
  testId,
}) => {
  const { locale } = useLanguage();
  const t = locale === 'en'
    ? {
        demo: 'Book a free demo',
        brochure: 'Download the brochure',
        whatsapp: 'Message us on WhatsApp',
      }
    : {
        demo: 'Prenota una demo gratuita',
        brochure: 'Scarica la brochure',
        whatsapp: 'Scrivici su WhatsApp',
      };

  const waMsg = locale === 'en'
    ? 'Hi!%20I%27d%20love%20to%20know%20more%20about%20Prime%20Vision.'
    : 'Ciao!%20Vorrei%20avere%20maggiori%20informazioni%20su%20Prime%20Vision.';

  const set = new Set(ctas);

  return (
    <section
      data-testid={testId}
      className="relative bg-white pt-28 md:pt-36 pb-14 md:pb-20 border-b border-slate-100"
    >
      <div className="pv-container">
        <div className="max-w-3xl">
          {eyebrow && (
            <div className="inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-teal" />
              <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {eyebrow}
              </span>
            </div>
          )}

          <h1
            data-testid={`${testId}-title`}
            className="mt-5 md:mt-6 font-heading font-bold tracking-normal leading-[1.05] text-pv-ink text-[36px] sm:text-[46px] md:text-[56px]"
          >
            {title_a}
            {title_b && (
              <>
                {' '}
                <br className="hidden sm:inline" />
                <span className="text-pv-teal">{title_b}</span>
              </>
            )}
          </h1>

          {subtitle && (
            <p className="mt-5 md:mt-6 text-slate-600 text-[15.5px] md:text-[17px] leading-[1.65] max-w-2xl">
              {subtitle}
            </p>
          )}

          {ctas.length > 0 && (
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-5">
              {set.has('demo') && (
                <Link
                  to="/bookacall"
                  onClick={() => setLeadSource(`${source}-demo`)}
                  className="pv-btn-primary group justify-center"
                  data-testid={`${testId}-cta-demo`}
                >
                  {t.demo}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              )}

              {set.has('brochure') && (
                <Link
                  to="/brochure"
                  onClick={() => setLeadSource(`${source}-brochure`)}
                  className="pv-btn-download group justify-center"
                  data-testid={`${testId}-cta-brochure`}
                >
                  <Download className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5" />
                  <span>{t.brochure}</span>
                </Link>
              )}

              {set.has('whatsapp') && (
                <a
                  href={`https://wa.me/393520526856?text=${waMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setLeadSource(`${source}-whatsapp`)}
                  className="pv-link-quiet justify-center sm:justify-start"
                  data-testid={`${testId}-cta-whatsapp`}
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>{t.whatsapp}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
